/**
 * useVoiceAnswer — self-contained voice question answering.
 *
 * Wraps the platform's STT facade (src/services/speech/speechService.js —
 * Azure/Sarvam, provider-switchable by super admins) and the server TTS
 * endpoint (/api/generate_audio) behind one small hook, so a test component
 * can offer "listen to the question, answer by speaking" without inheriting
 * the 4,000-line QuestionSection voice stack.
 *
 * Contract:
 *   speakQuestion(text)  — plays the question aloud (server TTS, browser fallback)
 *   startAnswer()        — begins recognition; liveTranscript updates in real time
 *   stopAnswer()         — ends recognition; resolves with the final transcript
 *   resetAnswer()        — clears transcripts for a re-record
 *
 * Answers auto-stop at MAX_ANSWER_SECONDS so a stuck recognizer can never
 * block the test.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  createSpeechProvider,
  fetchActiveProviderName,
} from '../services/speech/speechService';

const MAX_ANSWER_SECONDS = 90;

export default function useVoiceAnswer() {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [finalTranscript, setFinalTranscript] = useState('');
  const [voiceError, setVoiceError] = useState('');

  // Refs owned by us, lent to the speech adapter (see services/speech/types.js)
  const recognizerRef = useRef(null);
  const audioStreamRef = useRef(null);
  const recognitionActiveRef = useRef(false);
  const recognitionRetryCount = useRef(0);
  const interviewActiveRef = useRef(true);
  const transcriptionRef = useRef('');

  const providerRef = useRef(null);
  const providerNameRef = useRef('azure');
  const audioElRef = useRef(null);
  const maxTimerRef = useRef(null);
  const stopResolverRef = useRef(null);

  useEffect(() => {
    interviewActiveRef.current = true;
    fetchActiveProviderName().then((name) => {
      providerNameRef.current = name;
    });
    return () => {
      interviewActiveRef.current = false;
      if (maxTimerRef.current) clearTimeout(maxTimerRef.current);
      try {
        providerRef.current?.cleanup?.({ force: true });
      } catch (e) { /* teardown must never throw */ }
      if (audioElRef.current) {
        audioElRef.current.pause();
        audioElRef.current = null;
      }
      try { window.speechSynthesis?.cancel(); } catch (e) { /* ignore */ }
    };
  }, []);

  // ── TTS ────────────────────────────────────────────────────────────────────
  const speakQuestion = useCallback(async (text) => {
    if (!text || isSpeaking) return;
    setIsSpeaking(true);
    try {
      const resp = await axios.post(
        '/api/generate_audio',
        { text },
        { responseType: 'blob', timeout: 12000 },
      );
      const url = URL.createObjectURL(resp.data);
      await new Promise((resolve) => {
        const audio = new Audio(url);
        audioElRef.current = audio;
        audio.onended = resolve;
        audio.onerror = resolve;
        audio.play().catch(resolve);
      });
      URL.revokeObjectURL(url);
    } catch (err) {
      // Server TTS unavailable — fall back to the browser voice
      try {
        await new Promise((resolve) => {
          const utterance = new SpeechSynthesisUtterance(text);
          utterance.onend = resolve;
          utterance.onerror = resolve;
          window.speechSynthesis.speak(utterance);
        });
      } catch (e) {
        console.warn('[useVoiceAnswer] TTS unavailable', e);
      }
    } finally {
      setIsSpeaking(false);
      audioElRef.current = null;
    }
  }, [isSpeaking]);

  // ── STT ────────────────────────────────────────────────────────────────────
  const ensureProvider = useCallback(() => {
    if (providerRef.current) return providerRef.current;
    providerRef.current = createSpeechProvider(providerNameRef.current, {
      recognizerRef,
      audioStreamRef,
      recognitionActiveRef,
      recognitionRetryCount,
      interviewActiveRef,
      transcriptionRef,
      setIsRecognising: setIsRecording,
      setRecognisingText: (t) => setLiveTranscript(t || ''),
      setTranscription: (t) => setFinalTranscript(t || ''),
      setErrorMessage: (m) => setVoiceError(m || ''),
      setConvTick: () => {},
      onUserMessage: () => {},
    });
    return providerRef.current;
  }, []);

  const stopAnswer = useCallback(async () => {
    if (maxTimerRef.current) {
      clearTimeout(maxTimerRef.current);
      maxTimerRef.current = null;
    }
    try {
      await providerRef.current?.stopRecognition?.();
    } catch (e) {
      console.warn('[useVoiceAnswer] stopRecognition failed', e);
    }
    setIsRecording(false);
    const transcript = (transcriptionRef.current || '').trim();
    setFinalTranscript(transcript);
    setLiveTranscript('');
    if (stopResolverRef.current) {
      stopResolverRef.current(transcript);
      stopResolverRef.current = null;
    }
    return transcript;
  }, []);

  const startAnswer = useCallback(async () => {
    setVoiceError('');
    transcriptionRef.current = '';
    setFinalTranscript('');
    setLiveTranscript('');
    try {
      const provider = ensureProvider();
      const ok = provider.reuseExisting?.() || (await provider.createNewRecognizer());
      if (ok === false) {
        setVoiceError('Microphone unavailable — check permissions and retry.');
        return false;
      }
      await provider.startRecognition();
      setIsRecording(true);
      maxTimerRef.current = setTimeout(() => {
        stopAnswer();
      }, MAX_ANSWER_SECONDS * 1000);
      return true;
    } catch (err) {
      console.error('[useVoiceAnswer] startAnswer failed', err);
      setVoiceError('Could not start voice capture — check your microphone.');
      setIsRecording(false);
      return false;
    }
  }, [ensureProvider, stopAnswer]);

  const resetAnswer = useCallback(() => {
    transcriptionRef.current = '';
    setFinalTranscript('');
    setLiveTranscript('');
    setVoiceError('');
  }, []);

  return {
    speakQuestion,
    isSpeaking,
    startAnswer,
    stopAnswer,
    resetAnswer,
    isRecording,
    liveTranscript,
    finalTranscript,
    voiceError,
    maxAnswerSeconds: MAX_ANSWER_SECONDS,
  };
}
